import { Divider, Popconfirm, Tag } from 'antd';
import { Link } from 'react-router-dom';
import {
  ContainerOutlined,
  EyeOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { SupervisionStatus } from './constants/tagColors';
import { deleteSupervision } from '../routes/supervisions/ShowSupervision/utils/deleteSupervision';

/**
 * Formats a pensioner's full name with civility.
 * @function
 * @param {Object} pensioner - Pensioner object
 * @param {Function} t - Translation function
 * @returns {string} Formatted full name
 */
const formatPensionerName = (pensioner, t) => {
  const civilityKey = pensioner?.civility
    ? `pensioners.tags.${pensioner.civility}`
    : '';
  const civilityText = civilityKey ? t(civilityKey) : '';
  return `${civilityText} ${pensioner?.last_name || ''} ${
    pensioner?.first_name || ''
  }`.trim();
};

/**
 * Creates supervision columns configuration for data tables.
 * This utility function eliminates code duplication by generating the common structure
 * used across multiple supervision list components.
 * @function
 * @param {Object} params - Configuration parameters
 * @param {Function} params.t - Translation function from useTranslation hook
 * @param {Function} params.dispatchAPI - API dispatch function
 * @param {Function} params.message - Error message function
 * @param {Function} params.setRefreshData - Function to set refresh data state
 * @param {boolean} params.refreshData - Current refresh data state
 * @returns {Array} Array of column configuration objects
 */
export const createSupervisionColumns = ({
  t,
  dispatchAPI,
  message,
  setRefreshData,
  refreshData
}) => [
  {
    title: t('supervisions.form.reference'),
    key: 'reference',
    dataIndex: 'reference'
  },
  {
    title: t('supervisions.form.pensioners'),
    key: 'pensioners',
    dataIndex: 'pensioners',
    render: (pensioners) => {
      if (pensioners?.length) {
        return pensioners
          .map((pensioner) => formatPensionerName(pensioner, t))
          .join(', ');
      }
      return '-';
    }
  },
  {
    title: t('supervisions.form.address.city'),
    key: 'petsitting_address',
    dataIndex: 'petsitting_address',
    render: (petsitting_address) => petsitting_address?.address?.city || '-'
  },
  {
    title: t('supervisions.form.start_date'),
    key: 'main_informations.start_date',
    dataIndex: 'main_informations',
    render: (main_informations) =>
      main_informations?.start_date
        ? dayjs(main_informations.start_date).format('DD/MM/YYYY')
        : '-'
  },
  {
    title: t('supervisions.form.end_date'),
    key: 'main_informations.end_date',
    dataIndex: 'main_informations',
    render: (main_informations) =>
      main_informations?.end_date
        ? dayjs(main_informations.end_date).format('DD/MM/YYYY')
        : '-'
  },
  {
    title: t('supervisions.form.status'),
    key: 'status',
    render: (status) => (
      <Tag color={SupervisionStatus[status]}>
        {t(`supervisions.tags.${status}`)}
      </Tag>
    ),
    dataIndex: 'status'
  },
  {
    key: 'action',
    align: 'right',
    fixed: 'right',
    width: 120,
    render: (record) => (
      <>
        <Link
          to={{
            pathname: `/supervisions/show/${record?._id}`
          }}
        >
          <EyeOutlined style={{ fontSize: 18 }} />
        </Link>
        {['WAITING', 'REQUEST', 'FINISHED'].includes(record?.status) && (
          <>
            <Divider type="vertical" />
            <Popconfirm
              title={t('datatable.column.action.archive.title')}
              okText={t('datatable.column.action.archive.ok')}
              okButtonProps={{ type: 'danger' }}
              cancelText={t('datatable.column.action.archive.cancel')}
              onConfirm={() =>
                deleteSupervision({
                  dispatchAPI,
                  id: record._id,
                  message,
                  setRefreshData,
                  refreshData
                })
              }
              icon={<WarningOutlined />}
            >
              <ContainerOutlined
                style={{ color: '#b51010', fontSize: 18 }}
                type="delete"
              />
            </Popconfirm>
          </>
        )}
      </>
    )
  }
];
